import { StatusPill, type StatusTone } from '@norvix/ui';

/**
 * Single source of truth for domain status → brand tone + label.
 *
 * Replaces the per-page `xxxColor()` helpers that each mapped statuses to MUI
 * chip colours with slightly different labels. Every back-office status pill
 * now renders through here, so tones and wording stay consistent app-wide and
 * match the Figma "chips" component (via <StatusPill>).
 *
 * The maps mirror the real API enums (see apps/backoffice/src/api/*.api.ts).
 * Unknown values fall back to a humanized neutral pill rather than throwing.
 */
export type StatusKind =
  | 'kyc'        // customer KYC lifecycle status
  | 'kycCase'    // KYC case status (application review header)
  | 'aml'        // AML alert status
  | 'recon'      // reconciliation run status
  | 'report'     // regulatory report job status
  | 'mfa'        // admin TOTP enrolment
  | 'onboarding' // onboarding application status
  | 'payment'    // payment / transaction status
  | 'risk'       // AML risk rating
  | 'subject';   // customer subject type (individual / business)

interface ToneLabel {
  tone: StatusTone;
  label: string;
}

function humanize(value: string): string {
  const s = value.replace(/[_-]+/g, ' ').trim().toLowerCase();
  return s.length === 0 ? '—' : s.charAt(0).toUpperCase() + s.slice(1);
}

/** Customer KYC lifecycle status (customers / dashboard / customer-360). */
const KYC: Record<string, ToneLabel> = {
  ACTIVE:       { tone: 'success', label: 'Active' },
  UNDER_REVIEW: { tone: 'warning', label: 'Under review' },
  PENDING_KYC:  { tone: 'neutral', label: 'Pending KYC' },
  PENDING_EDD:  { tone: 'warning', label: 'EDD' },
  EXPIRED:      { tone: 'neutral', label: 'Expired' },
  PROHIBITED:   { tone: 'error',   label: 'Prohibited' },
  // The customer-360 route reports the proto KycStatus enum names. Without
  // these, a REJECTED customer rendered as an uncoloured pill indistinguishable
  // from a pending one — the officer would read a refusal as "nothing yet".
  NOT_STARTED:  { tone: 'neutral', label: 'Not started' },
  PENDING:      { tone: 'warning', label: 'Pending' },
  APPROVED:     { tone: 'success', label: 'Approved' },
  REJECTED:     { tone: 'error',   label: 'Rejected' },
  EDD_REQUIRED: { tone: 'warning', label: 'EDD required' },
  UNSPECIFIED:  { tone: 'neutral', label: 'Unknown' },
  UNKNOWN:      { tone: 'neutral', label: 'Unknown' },
};

/** KYC case status, as the application review header shows it. */
const KYC_CASE: Record<string, ToneLabel> = {
  PENDING_REVIEW: { tone: 'warning', label: 'Pending review' },
  UNDER_REVIEW:   { tone: 'info',    label: 'Under review' },
  EDD_REQUIRED:   { tone: 'warning', label: 'EDD required' },
  APPROVED:       { tone: 'success', label: 'Approved' },
  REJECTED:       { tone: 'error',   label: 'Rejected' },
};

/** AML alert status (AmlAlertStatus). */
const AML: Record<string, ToneLabel> = {
  OPEN:         { tone: 'warning', label: 'Open' },
  UNDER_REVIEW: { tone: 'info',    label: 'Under review' },
  ESCALATED:    { tone: 'error',   label: 'Escalated' },
  DISMISSED:    { tone: 'neutral', label: 'Dismissed' },
  SAR_FILED:    { tone: 'error',   label: 'SAR filed' },
};

/** Reconciliation run status (ReconciliationRun.status). */
const RECON: Record<string, ToneLabel> = {
  COMPLETED: { tone: 'success', label: 'Completed' },
  PARTIAL:   { tone: 'warning', label: 'Partial' },
  FAILED:    { tone: 'error',   label: 'Failed' },
  PENDING:   { tone: 'info',    label: 'Pending' },
  RUNNING:   { tone: 'info',    label: 'Running' },
};

/** Regulatory report job status (ReportJobStatus). */
const REPORT: Record<string, ToneLabel> = {
  PENDING:   { tone: 'neutral', label: 'Pending' },
  RUNNING:   { tone: 'info',    label: 'Running' },
  COMPLETED: { tone: 'success', label: 'Completed' },
  FAILED:    { tone: 'error',   label: 'Failed' },
};

/** Admin-user TOTP enrolment (pass 'ENABLED' / 'DISABLED'). */
const MFA: Record<string, ToneLabel> = {
  ENABLED:  { tone: 'success', label: 'Enrolled' },
  DISABLED: { tone: 'neutral', label: 'Not enrolled' },
};

/** Onboarding application status (ApplicationStatus). */
const ONBOARDING: Record<string, ToneLabel> = {
  IN_PROGRESS:    { tone: 'info',    label: 'In progress' },
  SUBMITTED:      { tone: 'warning', label: 'Submitted' },
  APPROVED:       { tone: 'success', label: 'Approved' },
  DECLINED:       { tone: 'error',   label: 'Declined' },
  INFO_REQUESTED: { tone: 'warning', label: 'Info requested' },
};

/** Payment / transaction status (PaymentStatus). */
const PAYMENT: Record<string, ToneLabel> = {
  PENDING:    { tone: 'warning', label: 'Pending' },
  PROCESSING: { tone: 'info',    label: 'Processing' },
  SETTLED:    { tone: 'success', label: 'Settled' },
  FAILED:     { tone: 'error',   label: 'Failed' },
  RETURNED:   { tone: 'neutral', label: 'Returned' },
  RECALLED:   { tone: 'neutral', label: 'Recalled' },
  BLOCKED:    { tone: 'error',   label: 'Blocked' },
};

/**
 * AML risk rating. Rendered as an uncoloured MUI chip before, so a HIGH-risk
 * customer looked identical to a LOW-risk one on the review header.
 */
const RISK: Record<string, ToneLabel> = {
  LOW:        { tone: 'success', label: 'Risk: Low' },
  MEDIUM:     { tone: 'warning', label: 'Risk: Medium' },
  HIGH:       { tone: 'error',   label: 'Risk: High' },
  PROHIBITED: { tone: 'error',   label: 'Risk: Prohibited' },
  // Not rated yet: said plainly, never shown as the safest rating (DEV-160).
  UNKNOWN:    { tone: 'neutral', label: 'Risk: Unknown' },
};

/** Whether the customer is a natural person or a legal entity. */
const SUBJECT: Record<string, ToneLabel> = {
  INDIVIDUAL: { tone: 'neutral', label: 'Individual' },
  BUSINESS:   { tone: 'info',    label: 'Business' },
};

const MAPS: Record<StatusKind, Record<string, ToneLabel>> = {
  risk: RISK,
  subject: SUBJECT,
  kyc: KYC,
  kycCase: KYC_CASE,
  aml: AML,
  recon: RECON,
  report: REPORT,
  mfa: MFA,
  onboarding: ONBOARDING,
  payment: PAYMENT,
};

interface StatusBadgeProps {
  kind: StatusKind;
  value: string;
}

export function StatusBadge({ kind, value }: StatusBadgeProps): JSX.Element {
  const known = MAPS[kind][value];
  const tone: StatusTone = known?.tone ?? 'neutral';
  const label = known?.label ?? humanize(value);
  return <StatusPill tone={tone}>{label}</StatusPill>;
}
